"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchFaculty, fetchFacultyById, updateFacultyStatus, deleteFaculty } from "../store/slices/facultySlice"
import AddFaculty from "./AddFaculty"
import EditFaculty from "./EditFaculty"
import ViewFaculty from "./ViewFaculty"
import {
  Users,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Loader2,
  CheckCircle,
  UserCheck,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"

// --- Aesthetic Configuration ---
const PRIMARY_COLOR = "blue-600"
const PRIMARY_HOVER = "blue-800"

// Utility function for Status Badges
const getStatusBadge = (status) => {
  const normalizedStatus = status ? status.toLowerCase() : "pending review"
  switch (normalizedStatus) {
    case "active":
      return {
        text: "Active",
        bg: "bg-green-100",
        textCol: "text-green-800",
        icon: <CheckCircle className="w-3 h-3 mr-1" />,
      }
    case "inactive":
      return {
        text: "Inactive",
        bg: "bg-yellow-100",
        textCol: "text-yellow-800",
        icon: <X className="w-3 h-3 mr-1" />,
      }
    default:
      return {
        text: "Active",
        bg: "bg-gray-100",
        textCol: "text-gray-600",
        icon: <UserCheck className="w-3 h-3 mr-1" />,
      }
  }
}

const FacultyComponent = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { faculty, currentFaculty, loading, error } = useSelector((state) => state.faculty)
  const [editingFaculty, setEditingFaculty] = useState(null)
  const [viewingFaculty, setViewingFaculty] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") {
      dispatch(fetchFaculty())
    } else if (user?.role === "faculty") {
      const id = user.faculty_id || user.id
      if (id) {
        dispatch(fetchFacultyById(id))
      }
    }
  }, [dispatch, user])

  const handleStatusChange = (facultyId, status) => {
    dispatch(updateFacultyStatus({ id: facultyId, status }))
  }

  const handleDelete = (facultyId) => {
    if (window.confirm("Are you sure you want to delete this faculty member?")) {
      dispatch(deleteFaculty(facultyId))
    }
  }

  const filteredFaculty = faculty.filter((member) => {
    return (
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.faculty_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.mobile_no?.includes(searchTerm)
    )
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredFaculty.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedFaculty = filteredFaculty.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value))
    setCurrentPage(1) // Reset to first page when items per page changes
  }

  if (loading)
    return (
      <div className={`flex items-center justify-center py-12 text-${PRIMARY_COLOR}`}>
        <Loader2 className="animate-spin h-8 w-8" />
        <span className="ml-2 text-gray-600">Loading faculty data...</span>
      </div>
    )

  return (
    <div className="p-4 sm:p-0">
      {/* Header and Add Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <Users className={`w-8 h-8 text-${PRIMARY_COLOR}`} />
          <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">Faculty Management</h2>
        </div>

        {/* Only Admin/Super Admin/Account can add faculty */}
        {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white font-semibold py-2 px-5 rounded-xl shadow-md transition-all transform hover:scale-[1.02]`}
          >
            {showAddForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {showAddForm ? "Close Form" : "Add New Faculty"}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-xl shadow-sm">
          <p className="text-red-700 font-medium">Error: {error}</p>
        </div>
      )}

      {/* Admin/Super Admin/Account View: Search */}
      {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") && !showAddForm && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, ID, or mobile number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            />
          </div>
        </div>
      )}

      {/* Faculty Profile (Faculty Role) */}
      {user?.role === "faculty" && currentFaculty && (
        <div className="bg-white p-8 rounded-xl shadow-2xl border-l-4 border-blue-500">
          <ViewFaculty faculty={currentFaculty} isProfile={true} />
        </div>
      )}

      {/* Faculty List Table (Admin/Super Admin/Account Role) */}
      {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") && (
        <div className="space-y-6">
          {paginatedFaculty.length === 0 ? (
            <div className="bg-white p-12 rounded-xl shadow-lg text-center border-2 border-dashed border-gray-300">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-600">No Faculty Found</p>
              <p className="text-gray-500 mt-1">Try adjusting your search criteria.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                       <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Faculty ID
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Name
                      </th>
                     
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Mobile
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Institute
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {paginatedFaculty.map((member) => {
                      const badge = getStatusBadge(member.employment_status)
                      return (
                        <tr key={member.faculty_id} className="hover:bg-blue-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{member.faculty_id}</td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {member.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{member.phone || "N/A"}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {member.department_name || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.textCol}`}
                            >
                              {badge.icon}
                              {badge.text}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setViewingFaculty(member)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors group"
                                title="View Details"
                              >
                                <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                              <button
                                onClick={() => setEditingFaculty(member)}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-full transition-colors group"
                                title="Edit Faculty"
                              >
                                <Edit className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                              <button
                                onClick={() => handleDelete(member.faculty_id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors group"
                                title="Delete Faculty"
                              >
                                <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          currentPage === page
                            ? `bg-${PRIMARY_COLOR} text-white`
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                    }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Modal Overlay */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] sm:max-h-[90vh] md:max-h-[95vh] overflow-y-auto transform scale-100 transition-transform duration-300">
            <div className="p-8">
              <AddFaculty setShowAddForm={setShowAddForm} />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal Overlay */}
      {editingFaculty && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] sm:max-h-[90vh] md:max-h-[95vh] overflow-y-auto transform scale-100 transition-transform duration-300">
            <div className="p-8">
              <EditFaculty faculty={editingFaculty} setEditingFaculty={setEditingFaculty} />
            </div>
          </div>
        </div>
      )}

      {/* View Modal Overlay */}
      {viewingFaculty && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] sm:max-h-[90vh] md:max-h-[95vh] overflow-y-auto transform scale-100 transition-transform duration-300">
            <div className="p-8">
              <ViewFaculty faculty={viewingFaculty} setViewingFaculty={setViewingFaculty} isProfile={false} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FacultyComponent
